// Utility functions for user session management

/**
 * Get Ministry Agency ID from user session with fallback
 * @returns {number} Ministry Agency ID (defaults to 4 if not found)
 */
export const getMinistryAgencyId = () => {
    try {
        const storedData = localStorage.getItem('LPA_Admin')
        if (storedData) {
            const parsed = JSON.parse(storedData)
            return parsed?.data1?.ministryAgencyId || 4
        }
    } catch (error) {
        console.error('Error getting ministry agency ID:', error)
    }
    return 4 // Default ministry ID
}

/**
 * Get current user ID from user session
 * @returns {string} User ID (empty string if not found)
 */
export const getCurrentUserId = () => {
    try {
        const storedData = localStorage.getItem('LPA_Admin')
        if (storedData) {
            const parsed = JSON.parse(storedData)
            return parsed?.data1?.id || ''
        }
    } catch (error) {
        console.error('Error getting user ID:', error)
    }
    return ''
}

/**
 * Get Ministry Agency Name from user session
 * @returns {string} Ministry Agency Name (empty string if not found)
 */
export const getMinistryAgencyName = () => {
    try {
        const storedData = localStorage.getItem('LPA_Admin')
        if (storedData) {
            const parsed = JSON.parse(storedData)
            return parsed?.data1?.ministryAgency?.name || ''
        }
    } catch (error) {
        console.error('Error getting ministry agency name:', error)
    }
    return ''
}

/**
 * Get user data from session
 * @returns {object|null} User data object or null if not found
 */
export const getUserData = () => {
    try {
        const storedData = localStorage.getItem('LPA_Admin')
        if (storedData) {
            const parsed = JSON.parse(storedData)
            return parsed?.data1 || null
        }
    } catch (error) {
        console.error('Error getting user data:', error)
    }
    return null
}

/**
 * Check if user is authenticated
 * @returns {boolean} True if user is authenticated
 */
export const isAuthenticated = () => {
    try {
        const storedData = localStorage.getItem('LPA_Admin')
        return !!storedData
    } catch (error) {
        console.error('Error checking authentication:', error)
        return false
    }
}

/**
 * Get menu data from localStorage
 * @returns {array} Menu data array or empty array if not found
 */
export const getMenuData = () => {
    try {
        const storedData = localStorage.getItem('LPA_Menu')
        if (storedData) {
            const parsed = JSON.parse(storedData)
            return Array.isArray(parsed) ? parsed : []
        }
    } catch (error) {
        console.error('Error getting menu data:', error)
    }
    return []
}

/**
 * Get Bank ID from user session
 * @returns {string} Bank ID (empty string if not found)
 */
export const getBankId = () => {
    try {
        const storedData = localStorage.getItem('LPA_Admin')
        if (storedData) {
            const parsed = JSON.parse(storedData)
            const d1 = parsed?.data1
            if (!d1) return ''
            // Try different possible locations for bank ID (camelCase, PascalCase, nested bank object)
            const raw = d1.bankId ?? d1.BankId ?? d1.bank?.id ?? d1.bank?.bankId ?? d1.id ?? ''
            if (raw === null || raw === undefined) return ''
            const str = String(raw).trim()
            return str
        }
    } catch (error) {
        console.error('Error getting bank ID:', error)
    }
    return ''
}

/**
 * Get Employee ID from user session
 * @returns {number} Employee ID (0 if not found)
 */
export const getEmployeeId = () => {
    try {
        const storedData = localStorage.getItem('LPA_Admin')
        if (storedData) {
            const parsed = JSON.parse(storedData)
            
            // Debug logging to help troubleshoot
            console.log('Session data for employee ID:', {
                data1: parsed?.data1,
                employeeId: parsed?.data1?.employeeId,
                id: parsed?.data1?.id,
                userId: parsed?.data1?.userId,
                userType: parsed?.data1?.userType
            })
            
            // Try different possible locations for employee ID
            let employeeId = parsed?.data1?.employeeId || 
                            parsed?.data1?.id || 
                            parsed?.data1?.userId ||
                            null
            
            // If not found, return 0
            if (employeeId === null || employeeId === undefined) {
                console.warn('Employee ID not found in session data')
                return 0
            }
            
            // Convert to number if it's a string
            if (typeof employeeId === 'string') {
                // Handle string IDs that might have prefixes (e.g., 'emp-1234567890')
                // Extract numeric part if there's a prefix
                const numericMatch = employeeId.match(/\d+/)
                if (numericMatch) {
                    const parsedId = parseInt(numericMatch[0], 10)
                    if (!isNaN(parsedId) && parsedId > 0) {
                        return parsedId
                    }
                }
                // Try direct parsing
                const parsedId = parseInt(employeeId, 10)
                if (!isNaN(parsedId) && parsedId > 0) {
                    return parsedId
                }
                console.warn('Could not parse employee ID from string:', employeeId)
                return 0
            }
            
            // If it's already a number, return it (but ensure it's positive)
            if (typeof employeeId === 'number') {
                return employeeId > 0 ? employeeId : 0
            }
            
            console.warn('Employee ID is in unexpected format:', typeof employeeId, employeeId)
            return 0
        }
    } catch (error) {
        console.error('Error getting employee ID:', error)
    }
    return 0
}

/**
 * Debug function to log current session data
 */
export const debugSessionData = () => {
    try {
        const storedData = localStorage.getItem('LPA_Admin')
        if (storedData) {
            const parsed = JSON.parse(storedData)
            console.log('Current session data:', parsed)
            console.log('Ministry Agency ID:', parsed?.data1?.ministryAgencyId)
            console.log('User ID:', parsed?.data1?.id)
            console.log('User Type:', parsed?.data1?.userType)
            console.log('Bank ID:', parsed?.data1?.bankId || parsed?.data1?.bank?.id)
            return parsed
        } else {
            console.log('No session data found')
            return null
        }
    } catch (error) {
        console.error('Error debugging session data:', error)
        return null
    }
}
